import { dispatchAnalyticsEvent } from "@configs/analytics/dispatch";
import { EVENTS } from "@configs/analytics/events/constants";
import { resetRegistrationFormFocusOncePerSession } from "@configs/analytics/events/dedupe";
import { identifyUser, resetAnalyticsUser } from "@configs/analytics/identify";
import { PayloadAction } from "@reduxjs/toolkit";
import logger from "@utils/logger";
import { ProfileResponse, SelfEntityModel } from "kz-ui-sdk";
import { call, select, takeEvery } from "redux-saga/effects";
import { RootState } from "../index";
import { AuthSliceState, authSlice } from "./slices";

async function updateAnalyticsUser(self: SelfEntityModel | null | undefined, serverPrefix?: string) {
  if (!self?.member) return;
  await identifyUser({ member: self.member, serverPrefix });
}

// Saga to handle updateProfile action
function* handleUpdateProfile(action: PayloadAction<ProfileResponse | null>): Generator<any, void, any> {
  try {
    const authState: AuthSliceState = yield select((state: RootState) => state.auth);
    const { serverInfo } = yield select((state: RootState) => state.preferences);
    yield call(updateAnalyticsUser, authState.self, serverInfo?.prefix);
  } catch (error) {
    logger._console.error("Error updating analytics user properties:", error);
  }
}

// Saga to handle initAuthState action
function* handleInitAuthState(action: PayloadAction<any>): Generator<any, void, any> {
  try {
    const authState: AuthSliceState = yield select((state: RootState) => state.auth);
    const { serverInfo } = yield select((state: RootState) => state.preferences);
    yield call(updateAnalyticsUser, authState.self, serverInfo?.prefix);
  } catch (error) {
    logger._console.error("Error updating analytics user properties:", error);
  }
}

// Saga to handle logout action
function* handleLogout(action: PayloadAction<void>): Generator<any, void, any> {
  try {
    // Log logout event before resetting user context. Routed straight through
    // the provider (skipping `dispatchTrackedEvent`'s requestIdleCallback) so
    // it lands in the Statsig pending queue *before* `resetAnalyticsUser`
    // calls `flushPending({ force: true })` + `shutdown()`.
    dispatchAnalyticsEvent(EVENTS.LOGOUT);

    // Reset user context across all analytics providers
    yield call(resetAnalyticsUser);

    // Clear once-per-session flags related to registration focus
    yield call(resetRegistrationFormFocusOncePerSession);
  } catch (error) {
    logger._console.error("Error resetting analytics user context on logout:", error);
  }
}

// Main saga that watches for auth actions
export function* analyticsSaga() {
  yield takeEvery(authSlice.actions.updateProfile.type, handleUpdateProfile);
  yield takeEvery(authSlice.actions.initAuthState.type, handleInitAuthState);
  yield takeEvery(authSlice.actions.logout.type, handleLogout);
}
